import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { isAxiosError } from 'axios';
import TextareaAutosize from 'react-textarea-autosize';
import {
  Box,
  Button,
  Select,
  OutlinedInput,
  TextField,
  InputLabel,
  FormControl,
  MenuItem,
  Paper,
  Checkbox,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { EllipsisLoader } from '../EllipsisLoader';

import {
  RELATIONS,
  MEDEXP_RU,
  OVERTREATMENT_EN,
  OVERTREATMENT_EN_TMP,
} from '../../helpers/options';
import { getCodeList } from '../../services';

const AddLinkForm = ({
  currentLanguage,
  profiles,
  relationType,
  defaultCoefficient = '1',
  onSubmit,
  onCancel,
}) => {
  const [searchParams] = useSearchParams();

  const [data, setData] = useState(() => ({
    profile: [],
    code: '',
    code_name: '',
    related_code: '',
    related_code_name: '',
    coefficient: String(defaultCoefficient),
    comment: '',
  }));
  const [error, setError] = useState({});
  const [codeLoading, setCodeLoading] = useState({
    code: false,
    related_code: false,
  });

  const debounceTimeout = useRef(null);
  const abortControllers = useRef({});

  const profilesParam = searchParams.get('profiles');

  const { t } = useTranslation(['relations', 'promptbar', 'common']);

  useEffect(() => {
    if (!profiles.length) {
      return;
    }

    if (profilesParam) {
      setData(prev => ({
        ...prev,
        profile: profilesParam.split(','),
      }));
      return;
    }

    if (!profilesParam) {
      let defaultProfiles = [];
      if (relationType === RELATIONS.RELATED) {
        defaultProfiles = [MEDEXP_RU];
      }
      if (
        relationType === RELATIONS.COMPLEX ||
        relationType === RELATIONS.MAPPED
      ) {
        defaultProfiles = profiles
          .filter(
            pr =>
              pr.name === OVERTREATMENT_EN || pr.name === OVERTREATMENT_EN_TMP,
          )
          .map(p => p.name);
      }

      setData(prev => ({
        ...prev,
        profile: defaultProfiles,
      }));
      return;
    }
  }, [profiles, profilesParam, relationType]);

  const codeSchema = Yup.string().trim().required(t('promptbar:requiredField'));

  const removeError = name => {
    if (!error[name]) {
      return;
    }
    const tmpError = { ...error };
    delete tmpError[name];
    setError(tmpError);
  };

  const handleInputChange = event => {
    const { value, name } = event.target;

    setData(prev => ({ ...prev, [name]: value }));

    if (name === 'coefficient') {
      const errorMessage = validateCoefficient(value);
      setError(prev => ({ ...prev, [name]: errorMessage }));
    }
  };

  // валидация коэффициента
  const validateCoefficient = value => {
    if (value === '') {
      return t('promptbar:Please_enter_a_valid_number');
    }

    const num = Number(value);

    if (isNaN(num)) {
      return t('promptbar:Please_enter_a_valid_number');
    }

    if (num < 0 || num > 100) {
      return t('promptbar:Value_must_be_between_0_and_100');
    }

    if (!/^\d+(\.\d{1,3})?$/.test(value)) {
      return t('promptbar:Maximum_3_decimal_places_allowed');
    }

    return null;
  };

  // введение кода и получение наименований
  const handleCodeChange = event => {
    const { value, name } = event.target;
    const formattedValue = value.trim();
    setData(prev => ({ ...prev, [name]: value, [`${name}_name`]: '' }));
    removeError(name);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      let currentController;
      try {
        await codeSchema.validate(formattedValue, { abortEarly: false });

        setCodeLoading(prev => ({ ...prev, [name]: true }));

        // отменяем предыдущий запрос для этого поля
        if (abortControllers.current[name]) {
          abortControllers.current[name].abort();
        }

        currentController = new AbortController();
        abortControllers.current[name] = currentController;

        const response = await getCodeList(
          { codes: formattedValue },
          currentController.signal,
        );

        if (response) {
          const codeNames =
            response.data.find(el => el.code === formattedValue)?.names || [];

          const codeName =
            codeNames.find(el => el.language_name === currentLanguage)?.name ||
            '';
          setData(prev => ({ ...prev, [`${name}_name`]: codeName }));

          if (!codeName) {
            setError(prev => ({
              ...prev,
              [name]: t('promptbar:CodeNotExist'),
            }));
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        if (error.name === 'ValidationError') {
          console.error('Ошибка валидации:', error.message);
          const validationError = {};

          error.inner.forEach(error => {
            validationError[name] = error.message;
          });
          setError(prev => ({ ...prev, ...validationError }));
        } else if (isAxiosError(error)) {
          console.error(
            'Ошибка запроса:',
            error.response?.data || error.message,
          );
        } else {
          console.error('Неизвестная ошибка:', error.message);
        }
      } finally {
        if (abortControllers.current[name] === currentController) {
          setCodeLoading(prev => ({ ...prev, [name]: false }));
        }
      }
    }, 500);
  };

  // отправка форма создание кода
  const handleFormSubmit = () => {
    const selectedProfiles = profiles
      .filter(profile => data.profile.includes(profile.profile_name))
      .map(pr => ({ ...pr, name: pr.profile_name, profile_name: undefined }));
    const newData = {
      relation_type: relationType,
      code: data.code.trim(),
      related_code: data.related_code.trim(),
      profiles: selectedProfiles,
      coefficient: Number(data.coefficient),
      comment: data.comment,
    };
    onSubmit(newData);
  };

  const isDisabledSaveBtn =
    !data.code.trim() ||
    !data.code_name ||
    !data.related_code.trim() ||
    !data.related_code_name ||
    !data.profile?.length ||
    codeLoading.code ||
    codeLoading.related_code ||
    !data.coefficient.trim();

  return (
    <Box
      component="form"
      autoComplete="off"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '2.5rem',
          '& > :nth-last-of-type(1):nth-of-type(odd)': {
            gridColumn: 'span 2',
          },
        }}
      >
        <FormControl fullWidth variant="standard">
          <InputLabel htmlFor="TextField-0" shrink={false}>
            {t('common:dictionaryLanguage')}
          </InputLabel>
          <TextField
            id="TextField-0"
            disabled
            name="language"
            value={currentLanguage.toUpperCase()}
          />
        </FormControl>

        <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
          <InputLabel id="Select-profile-label" shrink={false}>
            {t('common:profile')}
          </InputLabel>
          <Select
            labelId="Select-profile-label"
            id="Select-profile"
            name="profile"
            multiple
            value={data.profile}
            onChange={handleInputChange}
            input={<OutlinedInput label={t('common:profile')} />}
            IconComponent={KeyboardArrowDownIcon}
            MenuProps={{ size: 'small', autoFocus: false }}
            renderValue={selected => selected.map(el => el).join('; ')}
          >
            {profiles.map(el => (
              <MenuItem key={el.id} value={el.profile_name}>
                <Checkbox
                  checked={data.profile.indexOf(el.profile_name) > -1}
                />
                <ListItemText primary={el.profile_name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth variant="standard">
          <InputLabel htmlFor="TextField-1" shrink={false}>
            {t('relations:table.relation_type')}
          </InputLabel>
          <TextField
            id="TextField-1"
            disabled
            name="relation_type"
            value={t(`relations:table.${relationType}_codes`)}
          />
        </FormControl>

        <FormControl fullWidth variant="standard">
          <InputLabel htmlFor="TextField-6" shrink={false}>
            {t('relations:table.coefficient')}
          </InputLabel>
          <TextField
            id="TextField-6"
            type="number"
            name="coefficient"
            value={data.coefficient}
            onChange={handleInputChange}
            inputProps={{ min: 0, max: 100, step: 1 }}
            error={Boolean(error.coefficient)}
            helperText={error.coefficient ?? null}
          />
        </FormControl>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '2.5rem',
          paddingTop: '2.5rem',
        }}
      >
        <FormControl fullWidth variant="standard">
          <InputLabel htmlFor="TextField-2" shrink={false}>
            {t('relations:table.code')}
          </InputLabel>
          <TextField
            id="TextField-2"
            name="code"
            value={data.code}
            onChange={handleCodeChange}
            error={Boolean(error.code)}
            helperText={error.code ?? null}
          />
        </FormControl>

        <FormControl fullWidth variant="standard">
          <InputLabel htmlFor="TextField-3" shrink={false}>
            {t('relations:table.code_name')}
          </InputLabel>
          <Paper id="TextField-3" className="textfield" elevation={0} readOnly>
            {codeLoading.code ? <EllipsisLoader /> : data.code_name}
          </Paper>
        </FormControl>

        <FormControl fullWidth variant="standard">
          <InputLabel htmlFor="TextField-4" shrink={false}>
            {relationType === RELATIONS.RELATED
              ? t('relations:table.related_code')
              : relationType === RELATIONS.MAPPED
                ? t('relations:table.mapped_code')
                : relationType === RELATIONS.COMPLEX
                  ? t('relations:table.complex_code')
                  : t('relations:table.code')}
          </InputLabel>
          <TextField
            id="TextField-4"
            name="related_code"
            value={data.related_code}
            onChange={handleCodeChange}
            error={Boolean(error.related_code)}
            helperText={error.related_code ?? null}
          />
        </FormControl>

        <FormControl fullWidth variant="standard">
          <InputLabel htmlFor="TextField-5" shrink={false}>
            {relationType === RELATIONS.RELATED
              ? t('relations:table.related_code_name')
              : relationType === RELATIONS.MAPPED
                ? t('relations:table.mapped_code_name')
                : relationType === RELATIONS.COMPLEX
                  ? t('relations:table.complex_code')
                  : t('relations:table.code_name')}
          </InputLabel>
          <Paper id="TextField-5" className="textfield" elevation={0} readOnly>
            {codeLoading.related_code ? (
              <EllipsisLoader />
            ) : (
              data.related_code_name
            )}
          </Paper>
        </FormControl>
      </Box>

      <Box
        sx={{
          paddingTop: '2.5rem',
        }}
      >
        <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
          <InputLabel id="Input-comment-label" shrink={false}>
            {t('relations:table.comment')}
          </InputLabel>
          <TextareaAutosize
            id="Input-comment"
            name="comment"
            value={data.text1}
            onChange={handleInputChange}
          />
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', mt: '2rem', justifyContent: 'center' }}>
        <Button
          type="button"
          color="secondary"
          sx={{ mr: 1 }}
          variant="contained"
          onClick={onCancel}
        >
          {t('promptbar:cancel')}
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleFormSubmit}
          disabled={
            isDisabledSaveBtn || Boolean(Object.values(error).some(el => el))
          }
        >
          {t('promptbar:save')}
        </Button>
      </Box>
    </Box>
  );
};

AddLinkForm.propTypes = {
  currentLanguage: PropTypes.string.isRequired,
  profiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  relationType: PropTypes.string.isRequired,
  defaultCoefficient: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default AddLinkForm;
